import { useCallback, useEffect, useMemo, useState } from "react";
import { controlToastState } from "risa-oasis-ui_v2";
import {
  BucketDoc,
  BucketRow,
  rowSignature,
  toBucketDoc,
} from "./data-model/bucketModel";
import { fetchBucketConfig, saveBuckets } from "./utils/bucketService";

const generateId = (): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `bucket-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

const toBucketRow = (doc: BucketDoc): BucketRow => ({
  id: generateId(),
  name: doc.name || "",
  sub_bucket: doc.sub_bucket || "",
  keywords: Array.isArray(doc.keywords) ? [...doc.keywords] : [],
});

export interface UseFirstStpBucketingResult {
  loading: boolean;
  saving: boolean;
  rows: BucketRow[];
  dirtyCount: number;
  isDirty: (id: string) => boolean;
  hasBucket: (name: string, subBucket: string) => boolean;
  updateRow: (id: string, patch: Partial<BucketRow>) => void;
  addBucket: (input: { name: string; sub_bucket: string }) => void;
  saveChanges: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const useFirstStpBucketing = (): UseFirstStpBucketingResult => {
  const [rows, setRows] = useState<BucketRow[]>([]);
  const [baselineSignatures, setBaselineSignatures] = useState<
    Record<string, string>
  >({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadBuckets = useCallback(async () => {
    setLoading(true);
    try {
      const docs = await fetchBucketConfig();
      const nextRows = docs.map(toBucketRow);
      setRows(nextRows);
      const sigs: Record<string, string> = {};
      nextRows.forEach((r) => {
        sigs[r.id] = rowSignature(r);
      });
      setBaselineSignatures(sigs);
    } catch (err) {
      console.error("[firstStpBucketing] failed to load buckets", err);
      controlToastState("first-stp-bucketing-load-failure");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBuckets();
  }, [loadBuckets]);

  const isDirty = useCallback(
    (id: string) => {
      const row = rows.find((r) => r.id === id);
      if (!row) return false;
      const baseline = baselineSignatures[id];
      // New rows (no baseline) are always dirty until first save.
      if (baseline === undefined) return true;
      return rowSignature(row) !== baseline;
    },
    [rows, baselineSignatures],
  );

  const dirtyCount = useMemo(
    () => rows.filter((r) => isDirty(r.id)).length,
    [rows, isDirty],
  );

  const hasBucket = useCallback(
    (name: string, subBucket: string) => {
      const n = name.trim().toLowerCase();
      const s = subBucket.trim().toLowerCase();
      return rows.some(
        (r) =>
          r.name.trim().toLowerCase() === n &&
          r.sub_bucket.trim().toLowerCase() === s,
      );
    },
    [rows],
  );

  const updateRow = useCallback((id: string, patch: Partial<BucketRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const addBucket = useCallback(
    ({ name, sub_bucket }: { name: string; sub_bucket: string }) => {
      setRows((prev) => [
        ...prev,
        {
          id: generateId(),
          name: name.trim(),
          sub_bucket: sub_bucket.trim(),
          keywords: [],
        },
      ]);
    },
    [],
  );

  const saveChanges = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const docs = rows.map(toBucketDoc).filter((d) => d.name.length > 0);
      await saveBuckets(docs);
      controlToastState("first-stp-bucketing-updated-successfully");
      await loadBuckets();
    } catch {
      controlToastState("first-stp-bucketing-update-failure");
    } finally {
      setSaving(false);
    }
  }, [rows, saving, loadBuckets]);

  return {
    loading,
    saving,
    rows,
    dirtyCount,
    isDirty,
    hasBucket,
    updateRow,
    addBucket,
    saveChanges,
    refresh: loadBuckets,
  };
};
